import { Store } from '@/store';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableCell,
  TableBorders,
  TableRow,
  ImageRun,
  WidthType,
  BorderStyle,
  Footer,
  AlignmentType,
  PageNumber,
  convertMillimetersToTwip,
} from 'docx';
import { saveAs } from 'file-saver';
import InquiryConstants from '@/components/inquiry/InquiryConstants';

//----------------------------------
// IMAGES
//----------------------------------
/*
const IMAGES = {
  RADIO: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABYAAAAWCAMAAADzapwJAAAAM1BMVEUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACjBUbJAAAAEXRSTlMAEOnjxbWmn4JcTUUpU5GQVC1P/0wAAAB9SURBVBjTdZFXDsMwDEOp4RmP3v+0RZvYSKCIX/YDRA3iFNUcmEOuhJs+rLO0VqZy35CiHOt9SKSLSrqVUpLzFxMeSvHvK/TEJB0g3r7bnwlVYaQVeVo8MkKxuARws7ixgx0Tp6UzoLcO+svy3qn8w9oYljrr+IU2VmhOxF97rQN/p7iGqAAAAABJRU5ErkJggg==',
  RADIO_SELECTED: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABYAAAAWBAMAAAA2mnEIAAAAKlBMVEUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAKE86IAAAADXRSTlMA+PKvVE0TEMHqhIPDd7unNAAAAGxJREFUGNNjAILSNUqnwhnAgEVJ9u5FJQcQk83wLggIJwDZmbJg9sVpQDZQGCIBVA0Uhkg4MBTdhQF1hiA4W5VhIZwtzdALZ99g0IWzLyGzkdUg60U2E9kuFDcg3IbqZoRfUPwI9vsOpW6Q3wG805DYZwDiNwAAAABJRU5ErkJggg==',
  CHECKBOX: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABYAAAAWBAMAAAA2mnEIAAAAD1BMVEUAAAAAAAAAAAAAAAAAAABPDueNAAAABHRSTlMADOwYWy67SAAAAB9JREFUGNNjUHGBAScGZ0EYMGFwZIABkWHFRvIjst8BHmUNrz1ftAgAAAAASUVORK5CYII=',
  CHECKBOX_SELECTED: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABYAAAAWAQMAAAD+ev54AAAABlBMVEUAAAAAAAClZ7nPAAAAAXRSTlPw/VsqegAAABRJREFUCNdjqP//g+H//z/UxCAzARQ3QHUObf5VAAAAAElFTkSuQmCC',
};
*/

const FULL_WIDTH = 11195;

//----------------------------------
// CELL STYLES
//----------------------------------
const CELL_STYLES = {
  SECTION_TITLE: {
    shading: { fill: 'C6D9F1' },
    borders: {
      top: { style: BorderStyle.NONE, size: 0 },
      bottom: { style: BorderStyle.SINGLE, size: 32, color: 'FFFFFF' },
      left: { style: BorderStyle.NONE, size: 0 },
      right: { style: BorderStyle.NONE, size: 0 },
    },
    margins: {
      top: convertMillimetersToTwip(0),
      bottom: convertMillimetersToTwip(0),
      left: convertMillimetersToTwip(1),
      right: convertMillimetersToTwip(0),
    },
  },
  GROUP_TITLE: {
    borders: {
      top: { style: BorderStyle.NONE, size: 0 },
      bottom: { style: BorderStyle.SINGLE, size: 32, color: 'FFFFFF' },
      left: { style: BorderStyle.NONE, size: 0 },
      right: { style: BorderStyle.NONE, size: 0 },
    },
    margins: {
      top: convertMillimetersToTwip(0),
      bottom: convertMillimetersToTwip(0),
      left: convertMillimetersToTwip(1),
      right: convertMillimetersToTwip(0),
    },
  },
  LABEL: {
    shading: { fill: 'DBE5F1' },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 48, color: 'DBE5F1' },
      bottom: { style: BorderStyle.SINGLE, size: 48, color: 'DBE5F1' },
      left: { style: BorderStyle.NONE, size: 0 },
      right: { style: BorderStyle.NONE, size: 0 },
      insideHorizontal: { style: BorderStyle.NONE, size: 0 },
      insideVertical: { style: BorderStyle.NONE, size: 0 },
    },
    margins: {
      top: convertMillimetersToTwip(0),
      bottom: convertMillimetersToTwip(0),
      left: convertMillimetersToTwip(1),
      right: convertMillimetersToTwip(0),
    },
  },
  VALUE: {
    shading: { fill: 'F6F6FF' },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 48, color: 'F6F6FF' },
      bottom: { style: BorderStyle.SINGLE, size: 48, color: 'F6F6FF' },
      left: { style: BorderStyle.SINGLE, size: 12, color: 'F6F6FF' },
      right: { style: BorderStyle.NONE, size: 0 },
    },
    margins: {
      top: convertMillimetersToTwip(0),
      bottom: convertMillimetersToTwip(0),
      left: convertMillimetersToTwip(1),
      right: convertMillimetersToTwip(0),
    },
  },
  WHITE_VALUE: {
    shading: { fill: 'FFFFFF' },
    borders: {
      top: { style: BorderStyle.NONE, size: 0 },
      bottom: { style: BorderStyle.NONE, size: 0 },
      left: { style: BorderStyle.NONE, size: 0 },
      right: { style: BorderStyle.NONE, size: 0 },
    },
    margins: {
      top: convertMillimetersToTwip(0),
      bottom: convertMillimetersToTwip(0),
      left: convertMillimetersToTwip(1),
      right: convertMillimetersToTwip(0),
    },
  },
  LEGEND: {
    shading: { fill: 'FFFFFF' },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 120, color: 'FFFFFF' },
      bottom: { style: BorderStyle.SINGLE, size: 120, color: 'FFFFFF' },
      left: { style: BorderStyle.NONE, size: 0 },
      right: { style: BorderStyle.NONE, size: 0 },
    },
  },
  COMMENT: {
    shading: { fill: 'F6F6FF' },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 48, color: 'FFFFFF' },
      bottom: { style: BorderStyle.NONE, size: 0 },
      left: { style: BorderStyle.NONE, size: 0 },
      right: { style: BorderStyle.NONE, size: 0 },
    },
    margins: {
      top: convertMillimetersToTwip(0),
      bottom: convertMillimetersToTwip(0),
      left: convertMillimetersToTwip(1),
      right: convertMillimetersToTwip(1),
    },
  },
};

//----------------------------------
// PARAGRAPH STYLES
//----------------------------------
const PARAGRAPH_STYLES = [
  {
    id: 'reportTitle',
    name: 'Report Title',
    basedOn: 'Normal',
    next: 'Normal',
    run: { size: 24, bold: true, font: 'Arial', color: '000000' },
    paragraph: { spacing: { before: 140, after: 140 } },
  },
  {
    id: 'title',
    name: 'Title',
    basedOn: 'Normal',
    next: 'Normal',
    run: { size: 20, bold: true, font: 'Arial', color: '000000' },
    paragraph: { spacing: { before: 140, after: 140 } },
  },
  {
    id: 'regular',
    name: 'Regular',
    basedOn: 'Normal',
    next: 'Normal',
    run: { size: 20, bold: false, font: 'Arial', color: '000000' },
    paragraph: { spacing: { before: 80, after: 52 } },
  },
  {
    id: 'italic',
    name: 'Italic',
    basedOn: 'Normal',
    next: 'Normal',
    run: { size: 20, bold: false, italics: true, font: 'Arial', color: '000000' },
    paragraph: { spacing: { before: 80, after: 50 } },
  },
  {
    id: 'footer',
    name: 'Regular',
    basedOn: 'Normal',
    next: 'Normal',
    run: { size: 20, bold: false, font: 'Arial', color: '000000' },
    paragraph: { spacing: { before: 0, after: 0 } },
  },
  {
    id: 'regular-bold',
    name: 'Regular Bold',
    basedOn: 'Normal',
    next: 'Normal',
    run: { size: 20, bold: true, font: 'Arial', color: '000000' },
    paragraph: { spacing: { before: 80, after: 50 } },
  },
  {
    id: 'empty',
    name: 'Empty',
    basedOn: 'Normal',
    next: 'Normal',
    run: { size: 16, bold: false, font: 'Arial', color: '000000' },
    paragraph: { spacing: { before: 80, after: 50 } },
  },
  {
    id: 'empty-small',
    name: 'Small Empty',
    basedOn: 'Normal',
    next: 'Normal',
    run: { size: 10, bold: false, font: 'Arial', color: '000000' },
    paragraph: { spacing: { before: 20, after: 20 } },
  },
];

//----------------------------------
// Variables
//----------------------------------
let showICFLegend = true;

/**
 * Word/Docx Export
 */
export default class InquiryExportWord {
  //---------------------------------------------------
  //
  //  API / Public Methods
  //
  //---------------------------------------------------
  static async export(inquiryId, exportData, secondarySignature, frontpage) {
    showICFLegend = true;
    const COLUMNS = {
      DEFAULT: [3890, 3959, 3346],
      ICF: [3890, 4409, 724, 724, 724, 724],
      ADVANCED_MATRIX: [3890, 1345, 1650, 1345, 1650, 1315],
      DIAGNOSIS: [3890, 3653, 3652],
    };

    const date = new Date();
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const exportDate = `${day}.${month}.${date.getFullYear()}`;

    const children = [];
    if (frontpage) {
      const headerWidth = 745.599;
      let headerHeight = 705.834;
      const { naturalWidth, naturalHeight } = await this.getNaturalImageDimensions(frontpage);
      if (naturalWidth > 0 && naturalHeight > 0) {
        const aspectRatio = naturalHeight / naturalWidth;
        headerHeight = headerWidth * aspectRatio;
      }

      children.push(new Table({
        borders: TableBorders.NONE,
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        columnWidths: [FULL_WIDTH],
        rows: [
          new TableRow({
            children: [
              new TableCell({
                width: { size: FULL_WIDTH, type: WidthType.DXA },
                children: [
                  new Paragraph({
                    children: [
                      new ImageRun({
                        data: frontpage,
                        transformation: {
                          width: headerWidth,
                          height: headerHeight,
                        },
                      }),
                    ],
                    alignment: AlignmentType.LEFT,
                  }),
                ],
                margins: { top: 0, bottom: 0, left: 0, right: 0 },
                borders: {
                  top: { style: BorderStyle.NONE, size: 0 },
                  bottom: { style: BorderStyle.SINGLE, size: 64, color: 'FFFFFF' },
                  left: { style: BorderStyle.NONE, size: 0 },
                  right: { style: BorderStyle.NONE, size: 0 },
                },
              }),
            ],
          }),
          new TableRow({
            children: [
              new TableCell({
                width: { size: FULL_WIDTH, type: WidthType.DXA },
                children: [new Paragraph({ text: `SAV-Bericht vom ${exportDate}`, style: 'reportTitle' })],
              }),
            ],
          }),
          new TableRow({
            children: [
              new TableCell({
                width: { size: FULL_WIDTH, type: WidthType.DXA },
                children: [new Paragraph({ style: 'regular' })],
              }),
            ],
          }),
        ],
      }));
    }

    let columnWidths = null;
    exportData.forEach((section, sectionIndex) => {
      const contentRows = [];
      let rows = null;

      (section.groups || []).forEach((group) => {
        if (group.type === 'icf') {
          columnWidths = COLUMNS.ICF;
          rows = this.createICF(group);
          rows.forEach(row => contentRows.push(row));
        } else if (group.type === 'radio-matrix-advanced') {
          columnWidths = COLUMNS.ADVANCED_MATRIX;
          rows = this.createAdvancedMatrix(group);
          rows.forEach(row => contentRows.push(row));
        } else if (group.type === 'diagnosis') {
          columnWidths = COLUMNS.DIAGNOSIS;
          if (group.label) {
            rows = this.createGroupHeader(group.label, columnWidths.length);
            rows.forEach(row => contentRows.push(row));
          }

          (group.inputs || []).forEach((input) => {
            if (input.type === 'diagnosis') {
              rows = this.createDiagnosis(input, group.label);
              rows.forEach(row => contentRows.push(row));
            } else {
              rows = this.createDefaultTextRow(input, columnWidths.length);
              rows.forEach(row => contentRows.push(row));
            }
          });
        } else {
          columnWidths = COLUMNS.DEFAULT;
          if (group.label) {
            rows = this.createGroupHeader(group.label, columnWidths.length);
            rows.forEach(row => contentRows.push(row));
          }

          (group.inputs || []).forEach((input) => {
            rows = this.createDefaultTextRow(input);
            rows.forEach(row => contentRows.push(row));
          });
        }
      });

      const table = new Table({
        borders: TableBorders.NONE,
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        columnWidths,
        rows: [
          ...this.createSectionHeader(section.heading, sectionIndex, columnWidths.length),
          ...contentRows,
        ],
      });
      children.push(table);
    });

    children.push(new Table({
      borders: TableBorders.NONE,
      width: { size: FULL_WIDTH, type: WidthType.DXA },
      columnWidths: [FULL_WIDTH],
      rows: [
        new TableRow({
          children: [
            new TableCell({
              width: { size: FULL_WIDTH, type: WidthType.DXA },
              children: [new Paragraph({ style: 'regular' })],
              margins: { top: 50, bottom: 50, left: 0, right: 0 },
            }),
          ],
        }),
        /*
        new TableRow({
          children: [
            new TableCell({
              ...CELL_STYLES.VALUE,
              width: { size: FULL_WIDTH, type: WidthType.DXA },
              children: [new Paragraph({ style: 'regular' })],
              margins: { top: 160, bottom: 160, left: 160, right: 160 },
            }),
          ],
        }),
        */
      ],
    }));

    const signatures = [];
    if (Store?.extras?.signature) {
      const signature = await this.parseSignature(Store.extras.signature);
      signatures.push(signature);
      signatures.push(null);
    }

    if (secondarySignature) {
      const signature = await this.parseSignature(secondarySignature);
      signatures.push(signature);
    } else {
      signatures.push(null);
    }

    if (signatures.length > 0) {
      children.push(new Table({
        borders: TableBorders.NONE,
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        columnWidths: [3732, 3732, 3731],
        rows: [
          new TableRow({
            children: [
              new TableCell({
                width: { size: FULL_WIDTH, type: WidthType.DXA },
                columnSpan: 3,
                children: [new Paragraph({ style: 'regular' })],
                margins: { top: 50, bottom: 50, left: 0, right: 0 },
              }),
            ],
          }),
          new TableRow({
            children: signatures.map(sig => new TableCell({
              width: { size: 3731, type: WidthType.DXA },
              children: [new Paragraph({ children: sig, style: 'regular' })],
              margins: { top: 0, bottom: 0, left: 0, right: 0 },
            })),
          }),
        ],
      }));
    }

    children.push(new Paragraph({ style: 'regular' }));

    const doc = new Document({
      styles: { paragraphStyles: PARAGRAPH_STYLES },
      sections: [
        {
          properties: {
            titlePage: true,
            page: {
              margin: {
                top: 300,
                right: 350,
                bottom: 300,
                left: 350,
              },
              footerDistance: 300,
            },
          },
          children,
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      children: [PageNumber.CURRENT, '/', PageNumber.TOTAL_PAGES],
                    }),
                  ],
                  style: 'footer',
                  alignment: AlignmentType.RIGHT,
                }),
              ],
            }),
            first: new Footer({
              children: [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: InquiryConstants.PRIVACY_NOTICE,
                      transformation: {
                        width: 747.473,
                        height: 133.611,
                      },
                    }),
                  ],
                  alignment: AlignmentType.LEFT,
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      children: [PageNumber.CURRENT, '/', PageNumber.TOTAL_PAGES],
                    }),
                  ],
                  style: 'footer',
                  alignment: AlignmentType.RIGHT,
                }),
              ],
            }),
          },
        },
      ],
    });

    Packer.toBlob(doc).then((blob) => {
      // saveAs from FileSaver will download the file
      const filename = this.getFilename(exportData, inquiryId, 'docx');
      saveAs(blob, filename);
    });
  }

  //---------------------------------------------------
  //
  //  Private Methods
  //
  //---------------------------------------------------
  /** @private */
  static async parseSignature(ops) {
    const kids = [];
    for (let i = 0, n = ops.length; i < n; i += 1) {
      const op = ops[i];
      if (op.insert?.image) {
        let aspectRatio = 1;
        // eslint-disable-next-line no-await-in-loop
        const { naturalWidth, naturalHeight } = await this.getNaturalImageDimensions(op.insert.image);
        if (naturalWidth > 0 && naturalHeight > 0) {
          aspectRatio = naturalHeight / naturalWidth;
        }
        kids.push(new ImageRun({
          data: op.insert.image,
          transformation: { width: 60 / aspectRatio, height: 60 },
        }));
      } else {
        const lines = op.insert;
        const attributes = op.attributes || {};
        if (op.insert.includes('\n')) {
          const runs = lines.split('\n').map(line => new TextRun({
            break: 1,
            text: line,
            bold: attributes.bold || false,
            italic: attributes.italic || false,
          }));
          kids.push(...runs);
        } else {
          kids.push(new TextRun({
            text: op.insert,
            bold: attributes.bold || false,
            italic: attributes.italic || false,
          }));
        }
      }
    }
    return kids;
  }

  /** @private */
  static getFilename(exportData, inquiryid, file_ext) {
    const findObjectById = targetId => exportData.flatMap(obj => obj.groups || [])
      .flatMap(group => group.inputs || [])
      .find(input => input.id === targetId);

    /*
    let psychologist = findObjectById('9c9afd9b-7a31-4c81-a236-aac8f1d543dc');
    psychologist = (psychologist) ? `${psychologist.value} - ` : '';
    */

    let firstname = findObjectById('9490f6ea-220b-4d71-86b5-b6129887d008');
    firstname = (firstname) ? `${firstname.value} ` : '';
    const lastname = findObjectById('973834ac-3158-46be-8285-820d6545ac99');
    const fullname = (lastname) ? `${firstname}${lastname.value} - ` : '';

    return `SAV Bericht - ${fullname}${inquiryid}.${file_ext}`;
  }

  /** @private */
  static createSectionHeader(label, index, columnSpan = 2) {
    const out = [];
    if (index > 0) {
      out.push(new TableRow({
        children: [
          new TableCell({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            columnSpan,
            children: [new Paragraph({ style: 'regular' })],
          }),
        ],
      }));
    }

    out.push(new TableRow({
      children: [
        new TableCell({
          ...CELL_STYLES.SECTION_TITLE,
          width: { size: FULL_WIDTH, type: WidthType.DXA },
          columnSpan,
          children: [new Paragraph({ text: label, style: 'title' })],
        }),
      ],
    }));
    return out;
  }

  /** @private */
  static createGroupHeader(label, columnSpan = 2) {
    return [
      new TableRow({
        children: [
          new TableCell({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            columnSpan,
            children: [new Paragraph({ style: 'empty-small' })],
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            ...CELL_STYLES.GROUP_TITLE,
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            columnSpan,
            children: [new Paragraph({ text: label, style: 'title' })],
          }),
        ],
      }),
    ];
  }

  /** @private */
  static createDefaultTextRow(input, columnSpan = 3) {
    const tmp = document.createElement('div');
    tmp.innerHTML = input.label;
    const exportData = input.export;
    let label = tmp.textContent || tmp.innerText;
    if (exportData) {
      label = exportData.word || exportData.default;
    }

    if (input.exportStyle === 'fullwidth') {
      return [new TableRow({
        children: [
          new TableCell({
            ...CELL_STYLES.VALUE,
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            columnSpan,
            children: [new Paragraph({ children: this.convertLinebreaks(input.value), style: 'regular' })],
          }),
        ],
      })];
    }

    if (input.exportStyle === 'fullwidth-labeled') {
      return [
        new TableRow({
          children: [
            new TableCell({
              ...CELL_STYLES.GROUP_TITLE,
              width: { size: FULL_WIDTH, type: WidthType.DXA },
              columnSpan,
              children: [new Paragraph({ text: label, style: 'title' })],
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              ...CELL_STYLES.VALUE,
              width: { size: FULL_WIDTH, type: WidthType.DXA },
              columnSpan,
              children: [new Paragraph({ children: this.convertLinebreaks(input.value), style: 'regular' })],
            }),
          ],
        }),
      ];
    }

    return [new TableRow({
      children: [
        new TableCell({
          ...CELL_STYLES.LABEL,
          width: { size: 3144, type: WidthType.DXA },
          children: [new Paragraph({ text: label, style: 'regular' })],
        }),
        new TableCell({
          ...CELL_STYLES.VALUE,
          width: { size: 5856, type: WidthType.DXA },
          columnSpan: columnSpan - 1,
          children: [new Paragraph({ children: this.convertLinebreaks(input.value), style: 'regular' })],
        }),
      ],
    })];
  }

  /** @private */
  static createDiagnosis(input, grouplabel) {
    const out = [];
    if (input.value) {
      const heading = new TableRow({
        children: [
          new TableCell({
            width: { size: 3890, type: WidthType.DXA },
            children: [new Paragraph({ style: 'regular' })],
          }),
          new TableCell({
            ...CELL_STYLES.WHITE_VALUE,
            width: { size: 3678, type: WidthType.DXA },
            children: [new Paragraph({ text: 'Code/Bezeichnung', style: 'regular' })],
          }),
          new TableCell({
            width: { size: 3678, type: WidthType.DXA },
            children: [new Paragraph({ text: 'Fachstelle / Jahr', style: 'regular' })],
          }),
        ],
      });
      out.push(heading);
      (input.value || []).forEach((item) => {
        out.push(new TableRow({
          children: [
            new TableCell({
              ...CELL_STYLES.LABEL,
              width: { size: 3890, type: WidthType.DXA },
              children: [new Paragraph({ text: this.cleanString(item.diagnosis), style: 'regular' })],
            }),
            new TableCell({
              ...CELL_STYLES.WHITE_VALUE,
              width: { size: 3678, type: WidthType.DXA },
              children: [new Paragraph({ text: this.cleanString(item.dsm), style: 'regular' })],
            }),
            new TableCell({
              width: { size: 3678, type: WidthType.DXA },
              children: [new Paragraph({ text: this.cleanString(item.specialist), style: 'regular' })],
            }),
          ],
        }));
      });
      out.push(new TableRow({
        children: [
          new TableCell({
            columnSpan: 3,
            width: { size: 11295, type: WidthType.DXA },
            children: [new Paragraph({ style: 'regular' })],
          }),
        ],
      }));
    } else {
      out.push(
        new TableRow({
          children: [
            new TableCell({
              columnSpan: 3,
              width: { size: 11295, type: WidthType.DXA },
              children: [new Paragraph({ text: `Keine ${grouplabel}`, style: 'regular' })],
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              columnSpan: 3,
              width: { size: 11295, type: WidthType.DXA },
              children: [new Paragraph({ style: 'regular' })],
            }),
          ],
        }),
      );
    }
    return out;
  }

  /** @private */
  static createICF(group) {
    const legend = '1 = kein Problem / Ressource, 2 = Problem leicht-mässig, 3 = Problem erheblich-voll, * = k.A., nicht spezif., nicht anwendb.';
    const out = [];
    if (showICFLegend) {
      out.push(new TableRow({
        children: [
          new TableCell({
            ...CELL_STYLES.LEGEND,
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            columnSpan: 6,
            children: [new Paragraph({ text: legend, style: 'italic', alignment: AlignmentType.CENTER })],
          }),
        ],
      }));
      showICFLegend = false;
    } else {
      out.push(new TableRow({
        children: [
          new TableCell({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            columnSpan: 6,
            children: [new Paragraph({ style: 'empty' })],
          }),
        ],
      }));
    }

    if (group.inputs.length > 1) {
      const withAssessment = group.inputs.length === 3;
      const data = {
        fallback: withAssessment ? group?.inputs[0].fallback : null,
        assessment: withAssessment ? group?.inputs[0]?.value : null,
        values: withAssessment ? group?.inputs[1]?.value || [] : group?.inputs[0]?.value || [],
        comment: withAssessment ? group?.inputs[2]?.value : group?.inputs[1]?.value,
      };

      out.push(new TableRow({
        children: [
          new TableCell({
            ...CELL_STYLES.LABEL,
            width: { size: 3144, type: WidthType.DXA },
            children: [new Paragraph({ text: group.label, style: 'regular-bold' })],
          }),
          new TableCell({
            ...CELL_STYLES.WHITE_VALUE,
            width: { size: 3526, type: WidthType.DXA },
            children: [new Paragraph({ style: 'regular' })],
          }),
          new TableCell({
            width: { size: 585, type: WidthType.DXA },
            children: [new Paragraph({ text: '1', style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 585, type: WidthType.DXA },
            children: [new Paragraph({ text: '2', style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 585, type: WidthType.DXA },
            children: [new Paragraph({ text: '3', style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 585, type: WidthType.DXA },
            children: [new Paragraph({ text: '*', style: 'regular', alignment: AlignmentType.CENTER })],
          }),
        ],
      }));

      const getICFCode = code => (code ? `(${code}) ` : '');

      (data.values || []).forEach((item, idx) => {
        if (idx === 0) {
          out.push(new TableRow({
            children: [
              new TableCell({
                ...CELL_STYLES.LABEL,
                width: { size: 3144, type: WidthType.DXA },
                children: [new Paragraph({ children: this.getICFAssessment(data.assessment, data.fallback), style: 'regular' })],
                rowSpan: data.values.length,
              }),
              new TableCell({
                ...CELL_STYLES.WHITE_VALUE,
                width: { size: 3526, type: WidthType.DXA },
                children: [new Paragraph({ text: `${getICFCode(item.code)}${item.label}`, style: 'regular' })],
              }),
              new TableCell({
                width: { size: 585, type: WidthType.DXA },
                children: [new Paragraph({ children: [this.getICFValue(1, item.value)], style: 'regular', alignment: AlignmentType.CENTER })],
              }),
              new TableCell({
                width: { size: 585, type: WidthType.DXA },
                children: [new Paragraph({ children: [this.getICFValue(2, item.value)], style: 'regular', alignment: AlignmentType.CENTER })],
              }),
              new TableCell({
                width: { size: 585, type: WidthType.DXA },
                children: [new Paragraph({ children: [this.getICFValue(3, item.value)], style: 'regular', alignment: AlignmentType.CENTER })],
              }),
              new TableCell({
                width: { size: 585, type: WidthType.DXA },
                children: [new Paragraph({ children: [this.getICFValue(4, item.value)], style: 'regular', alignment: AlignmentType.CENTER })],
              }),
            ],
          }));
        } else {
          out.push(new TableRow({
            children: [
              new TableCell({
                ...CELL_STYLES.WHITE_VALUE,
                width: { size: 3526, type: WidthType.DXA },
                children: [new Paragraph({ text: `${getICFCode(item.code)}${item.label}`, style: 'regular' })],
              }),
              new TableCell({
                width: { size: 585, type: WidthType.DXA },
                children: [new Paragraph({ children: [this.getICFValue(1, item.value)], style: 'regular', alignment: AlignmentType.CENTER })],
              }),
              new TableCell({
                width: { size: 585, type: WidthType.DXA },
                children: [new Paragraph({ children: [this.getICFValue(2, item.value)], style: 'regular', alignment: AlignmentType.CENTER })],
              }),
              new TableCell({
                width: { size: 585, type: WidthType.DXA },
                children: [new Paragraph({ children: [this.getICFValue(3, item.value)], style: 'regular', alignment: AlignmentType.CENTER })],
              }),
              new TableCell({
                width: { size: 585, type: WidthType.DXA },
                children: [new Paragraph({ children: [this.getICFValue(4, item.value)], style: 'regular', alignment: AlignmentType.CENTER })],
              }),
            ],
          }));
        }
      });

      if (data.comment !== undefined) {
        out.push(
          new TableRow({
            children: [
              new TableCell({
                width: { size: FULL_WIDTH, type: WidthType.DXA },
                columnSpan: 6,
                children: [new Paragraph({ style: 'empty-small' })],
              }),
            ],
          }),
          new TableRow({
            children: [
              new TableCell({
                ...CELL_STYLES.COMMENT,
                width: { size: FULL_WIDTH, type: WidthType.DXA },
                columnSpan: 6,
                children: [new Paragraph({ children: this.convertLinebreaks(data.comment || ''), style: 'regular' })],
              }),
            ],
          }),
        );
      }
      return out;
    }
    return [];
  }

  /** @private */
  static createAdvancedMatrix(group) {
    const out = [
      new TableRow({
        children: [
          new TableCell({
            ...CELL_STYLES.LABEL,
            width: { size: 3890, type: WidthType.DXA },
            children: [new Paragraph({ style: 'regular' })],
          }),
          new TableCell({
            ...CELL_STYLES.WHITE_VALUE,
            width: { size: 2809, type: WidthType.DXA },
            columnSpan: 2,
            children: [new Paragraph({ text: 'Aktuell', style: 'regular-bold', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 2809, type: WidthType.DXA },
            columnSpan: 2,
            children: [new Paragraph({ text: 'Anvisiert (1-2 Jahre)', style: 'regular-bold', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 1737, type: WidthType.DXA },
            children: [new Paragraph({ text: 'Schwerpunkt für Förderung', style: 'regular-bold', alignment: AlignmentType.CENTER })],
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            ...CELL_STYLES.LABEL,
            width: { size: 3890, type: WidthType.DXA },
            children: [new Paragraph({ style: 'regular' })],
          }),
          new TableCell({
            ...CELL_STYLES.WHITE_VALUE,
            width: { size: 1080, type: WidthType.DXA },
            children: [new Paragraph({ text: 'Alters- / Lehrplan-gemäss', style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 1729, type: WidthType.DXA },
            children: [new Paragraph({ text: 'Individualisiert', style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 1080, type: WidthType.DXA },
            children: [new Paragraph({ text: 'Alters- / Lehrplan-gemäss', style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 1729, type: WidthType.DXA },
            children: [new Paragraph({ text: 'Individualisiert', style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 1737, type: WidthType.DXA },
            children: [new Paragraph({ style: 'regular' })],
          }),
        ],
      }),
    ];

    ((group.inputs || [])[0].value || []).forEach((item) => {
      out.push(new TableRow({
        children: [
          new TableCell({
            ...CELL_STYLES.LABEL,
            width: { size: 3890, type: WidthType.DXA },
            children: [new Paragraph({ text: item.label, style: 'regular' })],
          }),
          new TableCell({
            ...CELL_STYLES.WHITE_VALUE,
            width: { size: 1080, type: WidthType.DXA },
            children: [new Paragraph({ children: [this.getAdvancedMatrixValue((item.value || [])[0], 1)], style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 1729, type: WidthType.DXA },
            children: [new Paragraph({ children: [this.getAdvancedMatrixValue((item.value || [])[0], 2)], style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 1080, type: WidthType.DXA },
            children: [new Paragraph({ children: [this.getAdvancedMatrixValue((item.value || [])[1], 1)], style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 1729, type: WidthType.DXA },
            children: [new Paragraph({ children: [this.getAdvancedMatrixValue((item.value || [])[1], 2)], style: 'regular', alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            width: { size: 1737, type: WidthType.DXA },
            children: [new Paragraph({ children: [this.getAdvancedMatrixValue((item.value || [])[2])], style: 'regular', alignment: AlignmentType.CENTER })],
          }),
        ],
      }));
    });

    if (group.inputs.length === 2) {
      out.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: FULL_WIDTH, type: WidthType.DXA },
              columnSpan: 6,
              children: [new Paragraph({ style: 'regular' })],
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              ...CELL_STYLES.COMMENT,
              width: { size: FULL_WIDTH, type: WidthType.DXA },
              columnSpan: 6,
              children: [new Paragraph({ children: this.convertLinebreaks(group.inputs[1].value), style: 'regular' })],
            }),
          ],
        }),
      );
    }
    return out;
  }

  //----------------------------------
  // Helper Methods
  //----------------------------------

  static cleanString(str) {
    // eslint-disable-next-line no-control-regex
    const regex = /((?:[\0-\x08\x0B\f\x0E-\x1F\uFFFD\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))/g;
    return String(str || '').replace(regex, '');
  }

  /** @private */
  static convertLinebreaks(str = '') {
    // eslint-disable-next-line no-param-reassign
    str = this.cleanString(str);
    return str.split('\n').map((line, index) => new TextRun({ break: index > 0 ? 1 : 0, text: line }));
  }

  /** @private */
  static getICFValue(expected, received) {
    return (expected === received)
      ? new TextRun('☒') // ■
      : new TextRun('☐'); // □
    /*
    return (expected === received)
      ? new CheckboxRun(true)
      : new CheckboxRun(false);

    return (expected === received)
      ? new ImageRun({ data: IMAGES.RADIO_SELECTED, transformation: { width: 11, height: 11 } })
      : new ImageRun({ data: IMAGES.RADIO, transformation: { width: 11, height: 11 } });
   */
  }

  /** @private */
  static getICFAssessment(value, fallback = null) {
    const variants = ['Indikation gegeben', 'Indikation nicht gegeben'];
    if (variants.includes(value)) {
      const index = variants.findIndex(v => v === value);
      if (index === 0) {
        return this.convertLinebreaks('☒ Indikation gegeben\n☐ Indikation nicht gegeben');
      }
      return this.convertLinebreaks('☐ Indikation gegeben\n☒ Indikation nicht gegeben');
    }

    if (fallback !== null && fallback === false) {
      return this.convertLinebreaks('☐ Indikation gegeben\n☐ Indikation nicht gegeben');
    }
    return this.convertLinebreaks('(kein Indikationsbereich)');
  }

  /** @private */
  static getAdvancedMatrixValue(value, position = null) {
    if (!position) {
      return (value !== undefined && value !== null)
        ? new TextRun('☒') // ■
        : new TextRun('☐'); // □
    }
    return (value === position)
      ? new TextRun('☒') // ●
      : new TextRun('☐'); // ○
  }

  /** @private */
  static getNaturalImageDimensions(base64) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight });
      };
      img.src = base64;
    });
  }
}
